<script setup lang="ts">
import {reactive, ref, computed, watchEffect} from "vue";
import {useRouter} from "vue-router";
import {useToast} from "primevue/usetoast";
import dayjs from "dayjs";
import {saveGroupQuestions} from "@/Services/group-questions";
import {Button, DatePicker, Drawer, Label} from "@/Components/Library";
import {useMainStore} from "@/Stores/store";
import GroupItem from "@/Pages/User/Groups/GroupItem.vue";

const router = useRouter();
const toast = useToast();
const $store = useMainStore();
const selectedGroup = computed(() =>
    $store.group?.id === router.currentRoute.value.query.group ? $store.group : null,
);

const props = defineProps<{question: any}>();
const visible = defineModel<boolean>("visible");
const emit = defineEmits<{
    (e: "cancel"): void;
    (e: "launched"): void;
}>();

const launchData = reactive({
    group_id: router.currentRoute.value.query.group || null,
    publish_from: props.question?.is("post") ? null : dayjs().toDate(),
    publish_to: props.question?.is("post") ? null : dayjs().add(8, "days").hour(0).minute(0).toDate(),
});

const launching = ref(false);

const launchQuestion = async () => {
    try {
        console.log(launchData, props.question, props.question?.is("post"));
        // return;
        // if (!launchData.publish_from || !launchData.publish_to) {
        //     toast.add({
        //         severity: "warn",
        //         summary: "Missing dates",
        //         detail: "Please select both publish from and publish to dates",
        //         life: 3000,
        //     });
        //     return;
        // }

        launching.value = true;
        await saveGroupQuestions(launchData.group_id, launchData, {questions: [props.question.id]});

        toast.add({
            severity: "success",
            summary: "Question launched",
            detail: "The question has been successfully launched to the group",
            life: 3000,
        });

        $store.feed.questions = [];
        $store.group.questions = [];
        emit("launched");
        emit("update:visible", false);

        await router.push({
            name: "feed",
            params: {group: launchData.group_id},
        });
    } catch (error) {
        console.error(error);
    } finally {
        launching.value = false;
    }
};

const handleCancel = () => {
    emit("cancel");
    visible.value = false;
};

watchEffect(() => {
    if (props.question?.is("post") || !selectedGroup.value?.params.allow_member_posts) {
        launchData.publish_from = null;
        launchData.publish_to = null;
    } else {
        launchData.publish_from = dayjs().toDate();
        launchData.publish_to = dayjs().add(8, "days").hour(0).minute(0).toDate();
    }
});
</script>
<template>
    <Drawer
        v-model:visible="visible"
        position="bottom"
        header="Launch Question"
        class="relative max-h-dvh max-w-3xl overflow-y-auto"
        :showCloseIcon="false"
    >
        <div class="flex flex-col pb-12">
            <div class="mb-6" v-if="selectedGroup">
                <Label>Group</Label>
                <GroupItem class="!px-0" :group="selectedGroup" :showMenu="false" />
            </div>
            <template v-if="!question.is('post') && !selectedGroup.params.allow_member_posts">
                <p class="mb-4">Set the publish dates to launch the question.</p>
                <div class="mb-4 flex flex-col">
                    <Label for="launch_publish_from" class="mb-1" required>Publish from</Label>
                    <DatePicker
                        id="launch_publish_from"
                        name="publish_from"
                        placeholder="Select a date to publish the question from"
                        fluid
                        showTime
                        hourFormat="24"
                        :min-date="new Date()"
                        v-model="launchData.publish_from"
                        :hideOnDateTimeSelect="true"
                        pt:pcinputtext:root:required
                        dateFormat="dd/mm/yy"
                    />
                </div>
                <div class="mb-6 flex flex-col">
                    <Label for="launch_publish_to" class="mb-1" required>Publish until</Label>
                    <DatePicker
                        id="launch_publish_to"
                        name="publish_to"
                        placeholder="Select a date to publish the question until"
                        fluid
                        showTime
                        hourFormat="24"
                        :min-date="launchData.publish_from ?? new Date()"
                        v-model="launchData.publish_to"
                        :hideOnDateTimeSelect="true"
                        pt:pcinputtext:root:required
                        dateFormat="dd/mm/yy"
                    />
                </div>
            </template>
            <div class="flex flex-col gap-2">
                <Button
                    @click="launchQuestion"
                    class="w-full bg-askr-green text-white"
                    :loading="launching"
                    :icon="{icon: 'mingcute:send-plane-fill'}"
                >
                    {{ launching ? "Launching..." : "Launch Question" }}
                </Button>
                <Button @click="handleCancel" class="w-full" variant="outlined">Cancel</Button>
            </div>
        </div>
    </Drawer>
</template>
